import React, { useEffect } from 'react';
import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import styled from 'styled-components';
import { PageMainLabel } from 'app/components/SHLabel';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import { SHToolTip } from 'app/components/SHToolTip';
import LiveRankingTable from './LiveRankingTable';
import Logo from 'app/assets/icons/yeda-logo.png';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { PageMainSubLabel } from 'app/components/labels';
import useTeamReportsApi from 'admin/hooks/useTeamReportsApi';
import { CommonLoader } from 'app/components/CommonLoader';
import { useNavigate } from 'react-router-dom';

type Props = {};
export interface breadCrumbDataModel {
    id: string;
    text?: string;
}

const LiveRankingPage = (props: Props) => {
    const { getTeamRankingDetails, teamRankingData, loading, downloadTeamRankingReport, isDownloading } = useTeamReportsApi();
    const navigate = useNavigate();

    // Define column widths
    const getColumnWidth = (field: string): number => {
        switch (field) {
            case 'teamCode':
            case 'totalPoint':
            case 'totalTop10':
                return 110; // Less width for these
            case 'teamName':
                return 300; // More width for team name
            default:
                return 220; // Increased width for show columns
        }
    };

    // Transform team data
    const transformTeamData = (teams: any[]) => {
        return teams?.map(team => {
            const showsObject: any = {};
            team?.showResults?.forEach((show: any) => {
                showsObject[show?.showId] = show?.points;
            });
            return {
                teamCode: team?.teamCode,
                teamName: team?.teamName,
                teamId: team?.teamId,
                totalPoint: team?.totalPoints,
                totalTop10: team?.top10Points,
                rank: team?.rank,
                ...showsObject,
            };
        });
    };

    // Get unique shows from all teams
    const getUniqueShows = (teams: any[]) => {
        const showsMap = new Map();
        teams?.forEach(team => {
            team?.showResults?.forEach((show: any) => {
                if (!showsMap.has(show?.showId)) {
                    showsMap.set(show?.showId, {
                        id: show?.showId,
                        name: show?.showName,
                        startDate: show?.showStartDate,
                        endDate: show?.showEndDate,
                        isNationalFinal: show?.isNationalFinal,
                    });
                }
            });
        });
        return Array.from(showsMap.values());
    };

    // Senior High School Team Result
    const seniorTeams = teamRankingData?.categories?.[0]?.teams || [];
    const updatedSeniorData = transformTeamData(seniorTeams);
    const seniorShows = getUniqueShows(seniorTeams);

    // Create a map of team names to URLs for lookup
    const seniorTeamUrlMap = seniorTeams.reduce((acc, team) => {
        if (team?.teamName && team?.teamUrl) {  // ← Guard both values
            acc[team.teamName] = team.teamUrl;
        }
        return acc;
    }, {} as Record<string, string>);

    const gridDataSenior = updatedSeniorData?.map(team => {
        const result: any = {
            teamCode: team.teamCode,
            teamName: team.teamName,
            totalPoint: team.totalPoint,
            totalTop10: team.totalTop10,
            _teamId: team.teamId, // Store as private property for navigation
        };

        seniorShows?.forEach((show: any) => {
            result[show.name] = {
                id: show.id,
                value: team[show.id] || 0,
                startDate: show.startDate,
                endDate: show.endDate,
                isNationalFinal: show.isNationalFinal,
            };
        });

        return result;
    });

    const columnsSenior =
        gridDataSenior && gridDataSenior.length > 0
            ? Object.keys(gridDataSenior[0])?.filter(key => !key.startsWith('_'))?.map((key) => {
                const columnDef: any = {
                    field: key,
                    title: key,
                    headerCell: (props: any) => (
                        <HeaderCell {...props} showInfo={gridDataSenior[0][key]} />
                    ),
                    cell: (props: any) => <DataCell {...props} teamUrlMap={seniorTeamUrlMap} dataItem={props.dataItem} />,
                    width: getColumnWidth(key),
                    locked: ['teamCode', 'teamName', 'totalPoint', 'totalTop10'].includes(key),
                };

                return columnDef;
            })
            : [];

    // Junior High School Team Result
    const juniorTeams = teamRankingData?.categories?.[1]?.teams || [];
    const updatedJuniorData = transformTeamData(juniorTeams);
    const juniorShows = getUniqueShows(juniorTeams);

    // Create a map of team names to URLs for lookup
    const juniorTeamUrlMap = juniorTeams.reduce((acc, team) => {
        if (team?.teamName && team?.teamUrl) {  // ← Guard both values
            acc[team.teamName] = team.teamUrl;
        }
        return acc;
    }, {} as Record<string, string>);

    const gridDataJunior = updatedJuniorData?.map(team => {
        const result: any = {
            teamCode: team.teamCode,
            teamName: team.teamName,
            totalPoint: team.totalPoint,
            totalTop10: team.totalTop10,
            _teamId: team.teamId, // Store as private property for navigation
        };

        juniorShows?.forEach((show: any) => {
            result[show.name] = {
                id: show.id,
                value: team[show.id] || 0,
                startDate: show.startDate,
                endDate: show.endDate,
                isNationalFinal: show.isNationalFinal,
            };
        });

        return result;
    });

    const columnsJunior =
        gridDataJunior && gridDataJunior.length > 0
            ? Object.keys(gridDataJunior[0])?.filter(key => !key.startsWith('_'))?.map((key) => {
                const columnDef: any = {
                    field: key,
                    title: key,
                    headerCell: (props: any) => (
                        <HeaderCell {...props} showInfo={gridDataJunior[0][key]} />
                    ),
                    cell: (props: any) => <DataCell {...props} teamUrlMap={juniorTeamUrlMap} dataItem={props.dataItem} />,
                    width: getColumnWidth(key),
                    locked: ['teamCode', 'teamName', 'totalPoint', 'totalTop10'].includes(key),
                };

                return columnDef;
            })
            : [];

    // Header and Data Cells
    const HeaderCell = (props: any) => {
        const { showInfo, field } = props;

        if (typeof showInfo === 'object' && 'value' in showInfo) {
            return (
                <SHToolTip position="top">
                    <TableHeaderWrapper
                        style={{
                            backgroundColor: showInfo.isNationalFinal ? '#8ee08e' : 'inherit',
                        }}
                    >
                        <div
                            className="title"
                            title={props?.title}
                            style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                width: '100%',
                                flex: 1,
                                lineHeight: '22px'
                            }}
                        >
                            {props?.title}
                        </div>
                        <div className="date">
                            {new Date(showInfo?.startDate).toLocaleDateString()}
                        </div>
                    </TableHeaderWrapper>
                </SHToolTip>
            );
        }

        let headerTitle;
        switch (field) {
            case 'teamCode':
                headerTitle = 'Code';
                break;
            case 'teamName':
                headerTitle = 'Team Name';
                break;
            case 'totalPoint':
                headerTitle = 'Tot. Pts.';
                break;
            case 'totalTop10':
                headerTitle = 'Tot. Top10';
                break;
            default:
                headerTitle = props?.title;
        }
        const isStaticField = ['teamCode', "teamName", 'totalPoint', 'totalTop10'].includes(field);
        return (
            <StaticTableHeaderWrapper>
                <div style={isStaticField ? {
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#475467',
                } : {}}>{headerTitle}</div>
            </StaticTableHeaderWrapper>
        );
    };


    const DataCell = (props: any) => {
        const { field, dataItem, className, style, teamUrlMap } = props;
        const cellData = dataItem[field];
        const isCenterAligned = ['teamCode', 'totalPoint', 'totalTop10'].includes(field);

        // Handle team name with optional link
        if (field === 'teamName') {
            const teamUrl = teamUrlMap?.[cellData];
            const teamId = dataItem._teamId;

            const handleTeamClick = () => {
                if (teamUrl && teamId) {
                    navigate(`/team-ranking/${teamId}`);
                }
            };

            return (
                <td className={className} style={{ ...style }}>
                    {teamUrl ? (
                        <TeamLink
                            onClick={handleTeamClick}
                            style={{ cursor: 'pointer' }}
                        >
                            {cellData}
                        </TeamLink>
                    ) : (
                        cellData
                    )}
                </td>
            );
        }

        if (typeof cellData === 'object' && cellData?.value !== undefined) {
            return (
                <TableDataWrapper style={{ textAlign: isCenterAligned ? 'center' : 'inherit' }}>
                    <div style={{ textAlign: isCenterAligned ? 'center' : 'inherit' }}>{cellData?.value}</div>
                </TableDataWrapper>
            );
        }

        return (
            <td className={className} style={{ ...style, textAlign: isCenterAligned ? 'center' : 'inherit' }}>
                {cellData}
            </td>
        );
    };
    const handleDownloadTeamRankingReport = async () => {
        try {
            const url = await downloadTeamRankingReport();

            // Extract filename from URL
            const filename = url.split('/').pop();

            fetch(url)
                .then(response => response.blob())
                .then(blob => {
                    const objectUrl = URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = filename!; // Use the extracted filename

                    // Append link to body (required for Firefox)
                    document.body.appendChild(link);

                    // Trigger download
                    link.click();

                    // Clean up
                    document.body.removeChild(link);
                    URL.revokeObjectURL(objectUrl);
                })
                .catch(error => {
                    console.error('Error downloading file:', error);
                });

            return url;
        } catch (error) {
            console.error('Error downloading team point report:', error);
            throw error;
        }
    };
    useEffect(() => {
        getTeamRankingDetails();
    }, []);

    return (
        <>   {loading && <CommonLoader loaderType="main" />}
            <StyledCommonPageWrapper style={{ marginBottom: '50px' }}>
                <HeaderWrapper>
                    <img src={Logo} alt="showeyda-logo" onClick={() => navigate(`/`)} />
                </HeaderWrapper>
                <HeaderWrapper $spacebetween>
                    <SubHeadingWrapper>
                        <PageMainLabel>Team Ranking</PageMainLabel>
                        <PageMainSubLabel>{teamRankingData?.season?.name}</PageMainSubLabel>
                        <PageMainSubLabel>
                            TEAM RESULTS - You may click on a team name below to see detailed team results
                        </PageMainSubLabel>
                    </SubHeadingWrapper>
                    <AdminCustomButton
                        buttonType="uploadFile"
                        isActive={false}
                        className="fill-button"
                        onClick={handleDownloadTeamRankingReport}
                        disabled={isDownloading}
                    >
                        {isDownloading ? "Downloading..." : "Download In Excel"}
                    </AdminCustomButton>
                </HeaderWrapper>
                {gridDataSenior.length > 0 && <>
                    <StyledTitleCardWrapper>
                        <HeadingWrapper className="heading-wrapper">
                            <PageMainLabel className="label-wrapper">
                                Sr High Team Results
                            </PageMainLabel>
                        </HeadingWrapper>
                    </StyledTitleCardWrapper>
                    {loading ? (
                        <TableSkeletonLoader rowContent={3} width={100} headingWidth={40} />
                    ) : (
                        <LiveRankingTable data={gridDataSenior || []} columns={columnsSenior} />
                    )}
                </>}
                {gridDataJunior.length > 0 && <>
                    <StyledTitleCardWrapper className="junior-high">
                        <HeadingWrapper className="heading-wrapper">
                            <PageMainLabel className="label-wrapper">
                                Jr High Team Results
                            </PageMainLabel>
                        </HeadingWrapper>
                    </StyledTitleCardWrapper>
                    {loading ? (
                        <TableSkeletonLoader rowContent={3} width={100} headingWidth={40} />
                    ) : (
                        <LiveRankingTable data={gridDataJunior || []} columns={columnsJunior} />
                    )}
                </>}
            </StyledCommonPageWrapper>
        </>
    );
};

export default LiveRankingPage;

const SubHeadingWrapper = styled.div`
  display: flex;
  justify-content: start;
  gap: 10px;
  align-items: start;
  flex-direction: column;
  margin-bottom: 20px;
`;

const HeaderWrapper = styled.div<{ $spacebetween?: boolean }>`
  display: flex;
  justify-content: ${({ $spacebetween }) =>
        $spacebetween ? "space-between" : "flex-start"};
  gap: 100px;
  align-items: ${({ $spacebetween }) =>
        $spacebetween ? "start" : "centre"};
  padding: 10px 0 30px 0;

  .fill-button {
    margin-right: 0px;
  }
`;

const TitleCardWrapper = styled.div`
  &.junior-high {
    margin-top: 52px;
  }
  margin: 0px 0px 0px 0px;
  background-color: #e7ecff;
  .heading-wrapper {
    padding: 14px 0px 14px 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    .label-wrapper {
      font-family: 'Inter';
      font-size: 16px;
      font-weight: 600;
      line-height: 14.52px;
      color: #000000;
      margin: unset !important;
    }
  }
`;

const StaticTableHeaderWrapper = styled.th`
  div {
    padding-left: 8px;
  }
`;

const TableHeaderWrapper = styled.th`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: 'Inter';
  font-size: 15px;
  font-weight: 600 !important;
  line-height: 14.52px;
  color: #475467;
  margin: unset !important;
  min-height: 80px;
  padding: 8px 4px !important;
  gap: 8px;

  .title {
    flex: 1;
    display: flex;
    align-items: flex-start;
  }

  .date {
    font-size: 13px;
    font-weight: 400 !important;
    white-space: nowrap;
    text-align: center;
    width: 100%;
  }
`;

const TableDataWrapper = styled.td`
  width: 100% !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-align: center !important;
`;

const TeamLink = styled.a`
  color: #0056b3 !important;
  text-decoration: underline !important;
  cursor: pointer;
  
  &:hover {
    color: #003d82 !important;
  }
  
  &:visited {
    color: #0056b3 !important;
  }
`;

const StyledTitleCardWrapper = styled(TitleCardWrapper)`
  background-color: #e7ecff;
`;

const StyledCommonPageWrapper = styled(CommonPageWrapper)`
  padding-top: 26px;
  margin-bottom: unset !important;
`;